import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import { ArticleBody, ArticleCard } from "@/components/articles/ArticleBody";

const setTag = (selector, attrs, create) => {
  let el = document.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v || ""));
  return el;
};
const meta = (attr, key, content) => setTag(`meta[${attr}="${key}"]`, { content }, () => { const m = document.createElement("meta"); m.setAttribute(attr, key); return m; });

function useArticleSeo(article) {
  useEffect(() => {
    if (!article) return;
    const url = window.location.href.split("?")[0];
    const image = article.illustration_url || article.image_url || "";
    const abs = image.startsWith("/") ? `${window.location.origin}${image}` : image;
    document.title = `${article.headline} · Underdog Stories`;
    meta("name", "description", article.standfirst);
    [["og:type", "article"], ["og:title", article.headline], ["og:description", article.standfirst], ["og:image", abs], ["og:url", url], ["og:site_name", "Underdog Podcasts"]].forEach(([k, v]) => meta("property", k, v));
    [["twitter:card", "summary_large_image"], ["twitter:title", article.headline], ["twitter:description", article.standfirst], ["twitter:image", abs]].forEach(([k, v]) => meta("name", k, v));
    setTag('link[rel="canonical"]', { href: url }, () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; });
    const ld = setTag('script[data-article-ld]', {}, () => { const s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-article-ld", "1"); return s; });
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Article", headline: article.headline, description: article.standfirst, image: abs ? [abs] : undefined, datePublished: article.published_at, dateModified: article.updated_at,
          author: { "@type": "Person", name: article.author || "Brett Hanson", url: `${window.location.origin}/authors/${article.author_slug || "brett-hanson"}`, worksFor: { "@type": "Organization", name: "Underdog Podcasts" } }, publisher: { "@type": "Organization", name: "Underdog Podcasts" }, mainEntityOfPage: url, wordCount: article.word_count },
        article.episode && { "@type": "PodcastEpisode", name: article.episode.title, url: `${window.location.origin}/episodes/${article.episode.id}`, datePublished: article.episode.published_at,
          associatedMedia: { "@type": "MediaObject", contentUrl: article.episode.audio_url }, partOfSeries: { "@type": "PodcastSeries", name: article.show?.name, url: `${window.location.origin}/shows/${article.show?.slug}` } },
        article.faq?.length > 0 && { "@type": "FAQPage", mainEntity: article.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      ].filter(Boolean),
    });
    return () => { document.title = "Underdog Podcasts"; ld.remove(); };
  }, [article]);
}

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setArticle(null); setMissing(false);
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
  }, [slug]);
  useArticleSeo(article);
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 w-full max-w-5xl mx-auto px-5 sm:px-8 pt-8 pb-32">
        <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="article-back-link"><ArrowLeft size={14} /> All stories</Link>
        {missing && <p className="text-center text-muted-foreground py-20" data-testid="article-missing">That story isn't here (yet).</p>}
        {!article && !missing && <div className="max-w-3xl mx-auto space-y-4"><Skeleton className="h-4 w-40" /><Skeleton className="h-16 w-full" /><Skeleton className="h-6 w-2/3" /><Skeleton className="h-72 w-full rounded-3xl" /></div>}
        {article && <ArticleBody article={article} />}
      </main>
      <Footer />
    </div>
  );
}

export function ArticlesIndex() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const show = params.get("show") || "";
  const [data, setData] = useState(null);
  useEffect(() => {
    document.title = "Underdog Stories · Articles from the Underdog Podcast Network";
    meta("name", "description", "Evergreen stories from Underdog Podcasts: where everyday expressions come from, the history behind football clubs, and the explanations behind the sports you follow.");
    api.get("/articles", { params: { type, show, limit: 48 } }).then((r) => setData(r.data));
  }, [type, show]);
  const setFilter = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };
  return (
    <div className="min-h-screen flex flex-col" data-testid="articles-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 pb-32">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">Underdog Stories</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none">Articles</h1>
            <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">Evergreen stories drawn from our episodes — the answers people search for, with the full conversation one tap away.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="articles-type-filters">
            <button onClick={() => setFilter("type", "")} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!type ? "bg-sky-400 text-[#070F1E]" : "bg-white/10 hover:bg-white/20"}`} data-testid="articles-filter-all">All</button>
            {data?.content_types.map((t) => <button key={t.id} onClick={() => setFilter("type", t.id)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${type === t.id ? "bg-sky-400 text-[#070F1E]" : "bg-white/10 hover:bg-white/20"}`} data-testid={`articles-filter-${t.id}`}>{t.name}</button>)}
          </div>
        </div>
        {show && <p className="mb-6 text-sm text-muted-foreground">Showing stories from one show · <button onClick={() => setFilter("show", "")} className="text-sky-400">clear</button></p>}
        {!data && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-3xl" />)}</div>}
        {data?.items.length === 0 && <p className="text-center text-muted-foreground py-24" data-testid="articles-empty">No stories published yet — check back soon.</p>}
        {data?.items.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">{data.items.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}</div>}
      </main>
      <Footer />
    </div>
  );
}
